'use client';

import { Edit3, MoreHorizontal, Pin, PinOff, Trash2 } from 'lucide-react';
import { formatRelativeTime } from '../../lib/relative-time';
import type { RouterOutputs } from '../../lib/trpc';
import { cn } from '../../lib/utils';
import { Button } from '../../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu';
import { UserAvatar } from '../../ui/user-avatar';
import { RichTextContent } from '../rich-text-content';

/** A note as the `notes` router returns it. Never redeclare this shape — see `.agents/rules/trpc.md`. */
export type Note = RouterOutputs['notes']['list'][number];

export interface NoteCardProps {
  note: Note;
  isReadOnly?: boolean;
  onEdit: (note: Note) => void;
  onTogglePin: (note: Note) => void;
  onDelete: (note: Note) => void;
  className?: string;
}

/**
 * The one way a note is displayed, on every surface.
 *
 * Author, relative timestamp, an edited marker, the pin state, the overflow menu, and the note
 * body — nothing here is per-parent-entity, because a note attached to a contact and a note
 * attached to a saved page are the same thing. Compose this; do not write a second one.
 */
export function NoteCard({
  note,
  isReadOnly = false,
  onEdit,
  onTogglePin,
  onDelete,
  className,
}: NoteCardProps) {
  const authorName = note.author?.fullName || note.author?.email || 'Unknown user';
  const isEdited = note.updatedAt !== note.createdAt;

  return (
    <article
      className={cn(
        'group rounded-xl border border-border bg-card p-3 shadow-xs transition-colors',
        note.isPinned && 'border-amber-300 dark:border-amber-500/40',
        className
      )}
    >
      <div className="mb-2 flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          {note.isPinned && <Pin className="h-3.5 w-3.5 shrink-0 text-amber-500" />}
          <UserAvatar user={note.author ?? undefined} size="xs" />
          <div className="min-w-0">
            <p className="truncate text-[11px] font-semibold text-foreground">{authorName}</p>
            <p
              className="text-[10px] text-muted-foreground"
              title={new Date(note.updatedAt * 1000).toLocaleString()}
            >
              {formatRelativeTime(note.updatedAt * 1000)}
              {isEdited ? ' · Edited' : ''}
            </p>
          </div>
        </div>

        {!isReadOnly && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0">
                <MoreHorizontal className="h-3.5 w-3.5" />
                <span className="sr-only">Note actions</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onEdit(note)}>
                <Edit3 className="mr-2 h-3.5 w-3.5" /> Edit
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onTogglePin(note)}>
                {note.isPinned ? (
                  <PinOff className="mr-2 h-3.5 w-3.5" />
                ) : (
                  <Pin className="mr-2 h-3.5 w-3.5" />
                )}
                {note.isPinned ? 'Unpin' : 'Pin'}
              </DropdownMenuItem>
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                onClick={() => onDelete(note)}
              >
                <Trash2 className="mr-2 h-3.5 w-3.5" /> Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      <RichTextContent
        className="prose prose-sm dark:prose-invert max-w-none break-words text-sm prose-p:my-1 prose-headings:my-2 prose-ul:my-1 prose-ol:my-1"
        html={note.content}
      />
    </article>
  );
}
